// src/components/UserLocation.ts

export const AYESEARCH_LOCATION_KEY = "ayesearch_user_location";
export const AYESEARCH_NETWORK_LOCATION_KEY = "ayesearch_network_location";
export const AYESEARCH_LOCATION_MAX_AGE_MS = 2 * 60 * 60 * 1000;

const LOCATION_SCHEMA_VERSION = 2;
const REVERSE_GEOCODE_URL =
  "https://api.bigdatacloud.net/data/reverse-geocode-client";
const WEATHER_API_URL = "https://api.ayesearch.com/api/ai/weather/";
const CLIENT_IP_QUERY = "__ayesearch_client_ip__";

export interface StoredUserLocation {
  version: 2;
  latitude: number;
  longitude: number;
  accuracy: number | null;
  updatedAt: number;
  source: "browser" | "network";
  displayCity?: string;
  displayRegion?: string;
  displayCountry?: string;
  displayLabel?: string;
}

export interface WeatherLocationContext {
  coordinates: string;
  displayLabel: string;
  location: StoredUserLocation;
}

type AnyRecord = Record<string, any>;

function finiteNumber(value: unknown): number | null {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function cleanText(value: unknown): string {
  return String(value ?? "").trim();
}

function regionLabel(data: AnyRecord): string {
  const code = cleanText(data?.principalSubdivisionCode);

  if (code && code.includes("-")) {
    const suffix = code.split("-").pop()?.trim() || "";
    if (suffix && suffix.length <= 4) return suffix.toUpperCase();
  }

  return cleanText(data?.principalSubdivision);
}

function normalizedName(value: unknown): string {
  return cleanText(value).toLowerCase();
}

function localityEntries(data: AnyRecord): AnyRecord[] {
  const administrative = Array.isArray(data?.localityInfo?.administrative)
    ? data.localityInfo.administrative
    : [];
  const informative = Array.isArray(data?.localityInfo?.informative)
    ? data.localityInfo.informative
    : [];

  return [...administrative, ...informative].filter(
    (item): item is AnyRecord => Boolean(item && typeof item === "object"),
  );
}

function looksLikeCounty(entry: AnyRecord): boolean {
  const description = normalizedName(entry?.description);
  const name = normalizedName(entry?.name);
  return description.includes("county") || name.endsWith(" county");
}

function looksLikeState(entry: AnyRecord): boolean {
  const description = normalizedName(entry?.description);
  return (
    description.includes("state") ||
    description.includes("province") ||
    description.includes("principal subdivision")
  );
}

function looksLikeCountry(entry: AnyRecord): boolean {
  return normalizedName(entry?.description).includes("country");
}

function looksLikeCity(entry: AnyRecord): boolean {
  const description = normalizedName(entry?.description);
  return (
    description.includes("city") ||
    description.includes("town") ||
    description.includes("municipality") ||
    description.includes("municipal") ||
    description.includes("incorporated")
  );
}

function administrativeCity(data: AnyRecord): string {
  const entries = localityEntries(data);

  const candidates = entries
    .filter((entry) => {
      const name = cleanText(entry?.name);
      if (!name) return false;
      if (looksLikeCounty(entry) || looksLikeState(entry) || looksLikeCountry(entry)) {
        return false;
      }
      return looksLikeCity(entry);
    })
    .sort((a, b) => {
      const aLevel = finiteNumber(a?.adminLevel) ?? -1;
      const bLevel = finiteNumber(b?.adminLevel) ?? -1;
      return bLevel - aLevel;
    });

  return cleanText(candidates[0]?.name);
}

function bestDisplayCity(data: AnyRecord): string {
  // No locality is hard-coded. For a precise user-triggered GPS request,
  // prefer the locality containing the coordinate before a broader nearby
  // population center.
  const locality =
    cleanText(data?.locality) ||
    cleanText(data?.localityName);

  if (locality) return locality;

  const municipality = administrativeCity(data);
  if (municipality) return municipality;

  return cleanText(data?.city);
}

async function reverseGeocodeCurrentDevice(
  latitude: number,
  longitude: number,
): Promise<
  Pick<
    StoredUserLocation,
    "displayCity" | "displayRegion" | "displayCountry" | "displayLabel"
  >
> {
  try {
    const url = new URL(REVERSE_GEOCODE_URL);
    url.searchParams.set("latitude", String(latitude));
    url.searchParams.set("longitude", String(longitude));
    url.searchParams.set("localityLanguage", "en");

    const response = await fetch(url.toString(), {
      method: "GET",
      cache: "no-store",
      headers: { Accept: "application/json" },
    });
    if (!response.ok) return {};

    const data = (await response.json()) as AnyRecord;
    const city = bestDisplayCity(data);
    const region = regionLabel(data);
    const country = cleanText(data?.countryName);

    const label =
      [city, region].filter(Boolean).join(", ") ||
      [city, country].filter(Boolean).join(", ");

    return {
      displayCity: city || undefined,
      displayRegion: region || undefined,
      displayCountry: country || undefined,
      displayLabel: label || undefined,
    };
  } catch (error) {
    console.warn("AyeSearch reverse geocoding unavailable:", error);
    return {};
  }
}

function providerDisplayLabel(location: AnyRecord): string {
  const name = cleanText(location?.name);
  const region = cleanText(location?.region);
  const country = cleanText(location?.country);
  return (
    [name, region].filter(Boolean).join(", ") ||
    [name, country].filter(Boolean).join(", ") ||
    country
  );
}

function saveLocationToKey(
  key: string,
  latitude: number,
  longitude: number,
  accuracy: number | null,
  source: "browser" | "network",
  display: Partial<StoredUserLocation> = {},
): StoredUserLocation {
  const payload: StoredUserLocation = {
    version: LOCATION_SCHEMA_VERSION,
    latitude,
    longitude,
    accuracy: Number.isFinite(Number(accuracy)) ? Number(accuracy) : null,
    updatedAt: Date.now(),
    source,
    displayCity: cleanText(display.displayCity) || undefined,
    displayRegion: cleanText(display.displayRegion) || undefined,
    displayCountry: cleanText(display.displayCountry) || undefined,
    displayLabel: cleanText(display.displayLabel) || undefined,
  };

  localStorage.setItem(key, JSON.stringify(payload));
  return payload;
}

export function saveUserLocation(
  latitude: number,
  longitude: number,
  accuracy: number | null,
  display: Partial<StoredUserLocation> = {},
): StoredUserLocation {
  return saveLocationToKey(
    AYESEARCH_LOCATION_KEY,
    latitude,
    longitude,
    accuracy,
    "browser",
    display,
  );
}

function saveNetworkLocation(
  latitude: number,
  longitude: number,
  display: Partial<StoredUserLocation> = {},
): StoredUserLocation {
  return saveLocationToKey(
    AYESEARCH_NETWORK_LOCATION_KEY,
    latitude,
    longitude,
    null,
    "network",
    display,
  );
}

export function parseStoredUserLocation(raw: string | null): StoredUserLocation | null {
  if (!raw) return null;

  try {
    const value = JSON.parse(raw) as Partial<StoredUserLocation>;
    const latitude = finiteNumber(value.latitude);
    const longitude = finiteNumber(value.longitude);
    const updatedAt = finiteNumber(value.updatedAt);

    // Version 2 intentionally invalidates the older location payload shape.
    // This prevents stale legacy values (including previously misresolved
    // countries/cities) from taking precedence after this deployment.
    if (
      value.version !== LOCATION_SCHEMA_VERSION ||
      latitude === null ||
      longitude === null ||
      updatedAt === null ||
      latitude < -90 ||
      latitude > 90 ||
      longitude < -180 ||
      longitude > 180
    ) {
      return null;
    }

    return {
      version: LOCATION_SCHEMA_VERSION,
      latitude,
      longitude,
      accuracy: finiteNumber(value.accuracy),
      updatedAt,
      source: value.source === "network" ? "network" : "browser",
      displayCity: cleanText(value.displayCity) || undefined,
      displayRegion: cleanText(value.displayRegion) || undefined,
      displayCountry: cleanText(value.displayCountry) || undefined,
      displayLabel: cleanText(value.displayLabel) || undefined,
    };
  } catch {
    return null;
  }
}

export function storedLocationIsFresh(value: StoredUserLocation | null): boolean {
  if (!value) return false;
  const age = Date.now() - value.updatedAt;
  return age >= 0 && age <= AYESEARCH_LOCATION_MAX_AGE_MS;
}

export function locationCoordinates(value: StoredUserLocation): string {
  return `${value.latitude.toFixed(6)},${value.longitude.toFixed(6)}`;
}

export function locationDisplayLabel(value: StoredUserLocation | null): string {
  if (!value) return "";
  return (
    cleanText(value.displayLabel) ||
    [cleanText(value.displayCity), cleanText(value.displayRegion)]
      .filter(Boolean)
      .join(", ")
  );
}

export function currentStoredLocationRaw(): string {
  return localStorage.getItem(AYESEARCH_LOCATION_KEY)?.trim() || "";
}

function currentStoredNetworkLocationRaw(): string {
  return localStorage.getItem(AYESEARCH_NETWORK_LOCATION_KEY)?.trim() || "";
}

export function freshStoredLocation(): StoredUserLocation | null {
  const parsed = parseStoredUserLocation(currentStoredLocationRaw());
  return storedLocationIsFresh(parsed) && parsed?.source === "browser" ? parsed : null;
}

function freshStoredNetworkLocation(): StoredUserLocation | null {
  const parsed = parseStoredUserLocation(currentStoredNetworkLocationRaw());
  return storedLocationIsFresh(parsed) && parsed?.source === "network" ? parsed : null;
}

export function freshStoredLocationCoordinates(): string {
  const parsed = freshStoredLocation();
  return parsed ? locationCoordinates(parsed) : "";
}

export async function requestFreshBrowserLocation(): Promise<StoredUserLocation> {
  if (!("geolocation" in navigator)) {
    throw new Error("Geolocation is not supported by this browser.");
  }

  if (!window.isSecureContext) {
    throw new Error("Precise location requires a secure HTTPS page.");
  }

  const position = await new Promise<GeolocationPosition>((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true,
      maximumAge: 0,
      timeout: 20000,
    });
  });

  const display = await reverseGeocodeCurrentDevice(
    position.coords.latitude,
    position.coords.longitude,
  );

  // A new precise GPS fix supersedes the approximate network cache.
  localStorage.removeItem(AYESEARCH_NETWORK_LOCATION_KEY);

  return saveUserLocation(
    position.coords.latitude,
    position.coords.longitude,
    position.coords.accuracy,
    display,
  );
}

export async function refreshLocationIfAllowed(): Promise<StoredUserLocation | null> {
  const stored = freshStoredLocation();
  if (stored) return stored;

  try {
    // Safari/iOS does not consistently expose navigator.permissions for
    // geolocation. Never trigger a surprise permission prompt here; only do a
    // silent refresh when the Permissions API explicitly says "granted".
    if (!navigator.permissions?.query) return null;

    const permission = await navigator.permissions.query({
      name: "geolocation" as PermissionName,
    });

    if (permission.state !== "granted") return null;
    return await requestFreshBrowserLocation();
  } catch {
    return null;
  }
}

export async function requestApproximateNetworkLocation(): Promise<StoredUserLocation | null> {
  const cached = freshStoredNetworkLocation();
  if (cached) return cached;

  try {
    const url = new URL(WEATHER_API_URL);
    url.searchParams.set("q", CLIENT_IP_QUERY);
    url.searchParams.set("days", "3");
    // Avoid any browser/intermediary reuse of another client's IP-derived
    // response. The backend also marks this response private/no-store.
    url.searchParams.set("_location_request", String(Date.now()));

    const response = await fetch(url.toString(), {
      method: "GET",
      credentials: "include",
      cache: "no-store",
      headers: {
        Accept: "application/json",
        "Cache-Control": "no-cache",
      },
    });

    if (!response.ok) return null;

    const data = (await response.json()) as AnyRecord;
    const location = data?.location;
    if (!location || typeof location !== "object") return null;

    const latitude = finiteNumber(location?.lat);
    const longitude = finiteNumber(location?.lon);
    if (latitude === null || longitude === null) return null;

    return saveNetworkLocation(latitude, longitude, {
      displayCity: cleanText(location?.name) || undefined,
      displayRegion: cleanText(location?.region) || undefined,
      displayCountry: cleanText(location?.country) || undefined,
      displayLabel: providerDisplayLabel(location) || undefined,
    });
  } catch (error) {
    console.warn("AyeSearch approximate network location unavailable:", error);
    return null;
  }
}

export async function getFreshWeatherLocationContext(): Promise<WeatherLocationContext | null> {
  // 1) Fresh precise GPS is always preferred.
  let location = freshStoredLocation();

  // 2) If location permission is already granted, silently refresh GPS.
  if (!location) {
    location = await refreshLocationIfAllowed();
  }

  // 3) If precise location is unavailable/denied, use the actual request IP.
  // This keeps mobile, incognito and first-load weather useful without forcing
  // a browser permission prompt.
  if (!location) {
    location = await requestApproximateNetworkLocation();
  }

  if (!location) return null;

  return {
    coordinates: locationCoordinates(location),
    displayLabel: locationDisplayLabel(location),
    location,
  };
}

export function compactLocationHint(
  value: StoredUserLocation,
): Pick<StoredUserLocation, "version" | "latitude" | "longitude" | "updatedAt" | "source"> {
  return {
    version: LOCATION_SCHEMA_VERSION,
    latitude: value.latitude,
    longitude: value.longitude,
    updatedAt: value.updatedAt,
    source: value.source,
  };
}

export function isNearMeQuery(query: string): boolean {
  return /\b(near me|my area|where i am|my location|locally|here)\b/i.test(query);
}

export function isCurrentLocationQuery(query: string): boolean {
  const value = String(query ?? "").trim();
  if (!value) return false;

  return (
    /\b(what(?:'s| is)?|where(?:'s| is)?)\s+(?:my|current)\s+location\b/i.test(value) ||
    /\bwhat(?:'s| is)?\s+my\s+(?:current\s+)?(?:city|town|area)\b/i.test(value) ||
    /\bwhere\s+am\s+i\b/i.test(value) ||
    /\buse\s+my\s+(?:geo\s*)?location\b/i.test(value)
  );
}
